import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  DesignTestInputDto,
  DesignTestOutputDto
} from '../../../models/job-request-iterations-dto.interface';

interface PycnometerReportValues {
  p1?: string;
  p2?: string;
  sampleWeight?: string;
  cellVolume?: string;
  referenceVolume?: string;
  profileName?: string;
}

@Component({
  selector: 'app-pycnometer-test-report',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './pycnometer-test-report.component.html',
  styleUrl: './pycnometer-test-report.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PycnometerTestReportComponent {
  @Input({ required: true }) test!: DesignTestInputDto;
  @Input({ required: true }) output!: DesignTestOutputDto;

  protected pycnometerReportValues(): PycnometerReportValues | null {
    const values = {
      p1: this.value(['run1p1', 'p1']),
      p2: this.value(['run1p2', 'p2']),
      sampleWeight: this.value(['sampleweight']),
      cellVolume: this.value(['cellvolume']),
      referenceVolume: this.value(['referencevolume']),
      profileName: this.value(['profilename'])
    };

    return Object.values(values).some(value => value !== undefined) ? values : null;
  }

  protected displayValue(value: string | undefined): string {
    return value ?? '—';
  }

  private value(names: string[]): string | undefined {
    const acceptedNames = new Set(names.map(name => this.normalize(name)));
    const parsedField = [...this.output.parsedValues, ...this.test.parsedInputValues].find(field =>
      acceptedNames.has(this.normalize(field.label)) && field.value?.trim());

    return parsedField?.value?.trim()
      || this.xmlValue(this.output.content, acceptedNames)
      || this.xmlValue(this.test.content, acceptedNames)
      || undefined;
  }

  private xmlValue(content: string | null, names: Set<string>): string | null {
    if (!content) return null;

    const document = new DOMParser().parseFromString(content, 'application/xml');
    if (document.querySelector('parsererror')) return null;

    for (const element of Array.from(document.getElementsByTagName('*'))) {
      if (element.localName.toLowerCase() === 'parameter') {
        if (!names.has(this.normalize(element.getAttribute('key')))) continue;
      } else if (!names.has(this.normalize(element.localName))) {
        continue;
      }

      if (element.children.length === 0) {
        const value = element.textContent?.trim();
        if (value) return value;
      }
    }

    return null;
  }

  private normalize(value: string | null | undefined): string {
    return (value ?? '').replace(/[\s_\-/,().:%]+/g, '').toLowerCase();
  }
}